// InviteAgencyAdminModal — TASK-N55 Phase 1.
//
// Mirror of CreateSuperAdminModal in AdminUsersPage.tsx (sub-option ii-a).
// POST /admin/tenants/:tenantId/agency-admin → returns temp_password ONCE.
// The inviting super-admin must share the password out-of-band; the receiving
// agency-admin is forced through must_change_password + TOTP setup on first login.

import { useState, type JSX, type FormEvent } from "react";
import { adminFetch, ApiError } from "../lib/api.js";

interface InviteAgencyAdminResponse {
  _id:           string;
  email:         string;
  full_name:     string;
  tenant_id:     string;
  role:          string;
  temp_password: string;
}

interface Props {
  tenantId: string;
  onClose:  () => void;
}

function ResultPanel({ result, onClose }: { result: InviteAgencyAdminResponse; onClose: () => void }): JSX.Element {
  const [copied, setCopied] = useState(false);
  function copy(): void {
    navigator.clipboard.writeText(result.temp_password)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })
      .catch(() => { /* clipboard unavailable */ });
  }
  return (
    <>
      <h2 className="text-lg font-semibold text-slate-900 mb-2">Save this temp password</h2>
      <p className="text-sm text-slate-500 mb-4">
        This password is shown ONCE and CANNOT be retrieved. Share it securely with{" "}
        <strong>{result.email}</strong>. They'll be forced to change it on first login and set up TOTP.
      </p>
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
        <div className="text-xs text-slate-500 mb-1">Temporary password</div>
        <code className="block text-base font-mono font-semibold text-slate-900 break-all">
          {result.temp_password}
        </code>
      </div>
      <div className="flex gap-2">
        <button onClick={copy}
          className="flex-1 py-2 px-3 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50">
          {copied ? "Copied!" : "Copy password"}
        </button>
        <button onClick={onClose}
          className="flex-1 py-2 px-3 rounded-lg bg-indigo-700 text-white text-sm font-medium hover:bg-indigo-800">
          Done
        </button>
      </div>
      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-3">
        This password will not be shown again.
      </p>
    </>
  );
}

interface FormPanelProps {
  email:      string;
  fullName:   string;
  error:      string | null;
  submitting: boolean;
  onEmail:    (v: string) => void;
  onFullName: (v: string) => void;
  onSubmit:   (e: FormEvent) => void;
  onClose:    () => void;
}

function FormPanel({ email, fullName, error, submitting, onEmail, onFullName, onSubmit, onClose }: FormPanelProps): JSX.Element {
  return (
    <>
      <h2 className="text-lg font-semibold text-slate-900 mb-4">Invite agency admin</h2>
      {error && (
        <p role="alert" className="mb-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-600">
          {error}
        </p>
      )}
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label htmlFor="aa-name" className="block text-sm font-medium text-slate-700 mb-1">Full name</label>
          <input id="aa-name" value={fullName} onChange={(e) => onFullName(e.target.value)} required
            placeholder="Jane Doe"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
        <div>
          <label htmlFor="aa-email" className="block text-sm font-medium text-slate-700 mb-1">Email</label>
          <input id="aa-email" type="email" value={email} onChange={(e) => onEmail(e.target.value)} required
            placeholder="jane@agency.com" autoComplete="off"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={submitting}
            className="flex-1 py-2 px-4 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            Cancel
          </button>
          <button type="submit" disabled={submitting}
            className="flex-1 py-2 px-4 rounded-lg bg-indigo-700 text-white text-sm font-medium hover:bg-indigo-800 disabled:opacity-50">
            {submitting ? "Inviting…" : "Invite"}
          </button>
        </div>
      </form>
    </>
  );
}

function mapInviteError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | null;
    if (err.status === 409) return "An agency admin with this email already exists in this tenant.";
    if (err.status === 404) return "Tenant not found.";
    if (typeof body?.error === "string") return body.error;
    return "Could not invite agency admin.";
  }
  return "Network error. Try again.";
}

export function InviteAgencyAdminModal({ tenantId, onClose }: Props): JSX.Element {
  const [email,    setEmail]    = useState("");
  const [fullName, setFullName] = useState("");
  const [error,    setError]    = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result,   setResult]   = useState<InviteAgencyAdminResponse | null>(null);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const data = await adminFetch<InviteAgencyAdminResponse>(
        `/admin/tenants/${tenantId}/agency-admin`,
        { method: "POST", body: JSON.stringify({ email: email.trim().toLowerCase(), full_name: fullName.trim() }) },
      );
      setResult(data);
    } catch (err) {
      setError(mapInviteError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        {result ? (
          <ResultPanel result={result} onClose={onClose} />
        ) : (
          <FormPanel
            email={email} fullName={fullName} error={error} submitting={submitting}
            onEmail={setEmail} onFullName={setFullName}
            onSubmit={(e) => { void handleSubmit(e); }}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}
